import { useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

import { useFormState } from '@/hooks/useFormState';
import { useSocialLogin } from '@/hooks/useSocialLogin';
import { emailLogin } from '@/services/authService';
import { validateLoginForm } from '@/utils/authValidation';
import {
  generateSSOLoginUrl,
  generateSSOLoginUrlWithEmail,
  getIsKuaishouEmail,
} from '@/utils/const';

import { AuthFormLayout } from '../common/AuthFormLayout';
import { Button } from '../common/Button';
import { FormInput } from '../common/FormInput';
import { TermsCheckbox } from '../common/TermsCheckbox';

interface LoginFormData {
  email: string;
  password: string;
  terms: boolean;
}

interface LoginFormProps {
  /** 受控：是否展示 SSO 界面（用于与页面标题等同步） */
  showSSOMode?: boolean;
  onSSOModeChange?: (value: boolean) => void;
}

// 获取query ticket并传递
const urlParams = new URLSearchParams(window.location.search);
const ticket = urlParams.get('ticket') || undefined;
const product = urlParams.get('product') || undefined;

export const LoginForm = ({
  showSSOMode: showSSOModeProp,
  onSSOModeChange,
}: LoginFormProps = {}) => {
  const { t } = useTranslation();
  const { handleGithubLogin, handleGoogleLogin } = useSocialLogin();

  const { values, errors, isLoading, setValue, setError, clearError, setLoading, reset } =
    useFormState<LoginFormData>({
      initialValues: {
        email: '',
        password: '',
        terms: true,
      },
      validate: validateLoginForm,
    });

  // 是否展示 SSO 登录界面（一旦因快手邮箱进入 SSO 界面，只有点击「使用其他方式登录」才回到普通界面）
  const [internalShowSSOMode, setInternalShowSSOMode] = useState(false);
  const isControlled = showSSOModeProp !== undefined && onSSOModeChange !== undefined;
  const showSSOMode = isControlled ? showSSOModeProp : internalShowSSOMode;
  const setShowSSOMode = isControlled ? onSSOModeChange : setInternalShowSSOMode;

  const isKuaishouEmail = getIsKuaishouEmail(values.email);

  useEffect(() => {
    if (isKuaishouEmail) {
      setShowSSOMode(true);
    }
  }, [isKuaishouEmail, setShowSSOMode]);

  // Handle email login
  const handleEmailLogin = async () => {
    const validationErrors = validateLoginForm(values);

    if (Object.keys(validationErrors).length > 0) {
      Object.entries(validationErrors).forEach(([field, error]) => {
        setError(field, error);
      });

      if (validationErrors.terms) {
        toast.error(t('loginForm.termsError'));
      }
      return;
    }

    try {
      setLoading(true);

      const response = await emailLogin({
        email: values.email,
        password: values.password,
        auth_ticket_id: ticket,
        product,
      });

      if (response.status === 200) {
        // 保留 URL 参数并刷新页面
        // eslint-disable-next-line no-self-assign
        window.location.href = window.location.href;
      } else {
        toast.error(response.message || t('loginForm.loginError'));
      }
    } catch (error: unknown) {
      const errorMessage = error instanceof Error ? error.message : t('loginForm.loginError');
      setError('general', errorMessage);
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const ssoLoginHref = generateSSOLoginUrl();

  // Handle SSO login for Kuaishou email
  const handleSSOLogin = () => {
    if (!isKuaishouEmail || !values.email.trim()) {
      return;
    }
    const ssoUrl = generateSSOLoginUrlWithEmail(values.email.trim().split('@')[0]);
    window.location.href = ssoUrl;
  };

  // Handle "Use other login method" - clear email and return to normal form
  const handleUseOtherLoginMethod = () => {
    setValue('email', '');
    setShowSSOMode(false);
    clearError('email');
    clearError('general');
  };

  // Handle social login with loading state and form reset
  const handleSocialLogin = async (loginFn: (ticket?: string, product?: string) => void) => {
    try {
      setLoading(true);
      // 在跳转前重置表单状态，清除输入内容和校验状态
      // 这样可以避免用户从第三方登录返回后，表单内容仍然保留的问题
      reset();
      // 延迟执行跳转，确保状态重置完成
      // 100ms 的延迟足够让 React 状态更新完成
      setTimeout(() => {
        loginFn(ticket, product);
      }, 100);
    } catch (_error) {
      setLoading(false);
    }
  };

  // Handle Enter key press
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !isLoading) {
      e.preventDefault();
      if (showSSOMode) {
        handleSSOLogin();
      } else {
        handleEmailLogin();
      }
    }
  };

  return (
    <AuthFormLayout
      onGithubLogin={() => handleSocialLogin(handleGithubLogin)}
      onGoogleLogin={() => handleSocialLogin(handleGoogleLogin)}
      isLoading={isLoading}
      generalError={errors.general}
      showSocialLogin={!showSSOMode}
    >
      <form
        onSubmit={(e) => {
          e.preventDefault();
          if (showSSOMode) {
            handleSSOLogin();
          } else {
            handleEmailLogin();
          }
        }}
        onKeyDown={handleKeyDown}
      >
        {/* Email input */}
        <FormInput
          type="email"
          name="email"
          id="login-email"
          autoComplete="username"
          value={values.email}
          onChange={(e) => {
            const newEmail = e.target.value;
            setValue('email', newEmail);
            clearError('email');
            clearError('general');
          }}
          placeholder={t('loginForm.emailPlaceholder')}
          error={errors.email}
          tabIndex={1}
        />

        {showSSOMode ? (
          <>
            {/* SSO Login button */}
            <div className="mt-[8px] flex flex-col gap-[8px]">
              <Button
                onClick={handleSSOLogin}
                disabled={isLoading || !values.email.trim()}
                isLoading={isLoading}
                tabIndex={2}
              >
                {t('loginForm.loginButton')}
              </Button>
            </div>

            {/* Use other login method link */}
            <div className="mt-[20px] flex flex-col items-center">
              <button
                type="button"
                onClick={handleUseOtherLoginMethod}
                className="text-[12px] font-medium text-black hover:text-black hover:opacity-60"
              >
                {t('loginForm.useOtherLoginMethod')}
              </button>
            </div>
          </>
        ) : (
          <>
            {/* Password input */}
            <FormInput
              type="password"
              name="password"
              id="login-password"
              autoComplete="current-password"
              value={values.password}
              onChange={(e) => {
                setValue('password', e.target.value);
                clearError('password');
                clearError('general');
              }}
              placeholder={t('loginForm.passwordPlaceholder')}
              error={errors.password}
              tabIndex={2}
            />

            {/* Terms agreement and login button */}
            <div className="flex flex-col gap-[8px]">
              <TermsCheckbox
                checked={values.terms}
                onChange={(e) => {
                  setValue('terms', e.target.checked);
                  clearError('terms');
                }}
                error={errors.terms}
              />

              <Button
                onClick={handleEmailLogin}
                disabled={isLoading}
                isLoading={isLoading}
                tabIndex={3}
              >
                {t('loginForm.loginButton')}
              </Button>

              <div className=" mt-1 flex justify-center">
                <a
                  href={ssoLoginHref}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="  text-[12px] font-medium text-black hover:text-black hover:opacity-60"
                >
                  {t('loginForm.ssoLoginLinkTitle')}
                </a>
              </div>
            </div>

            {/* Bottom links */}
            <div className="mt-[20px] flex flex-col items-center gap-[4px]">
              <p className="text-[12px] font-medium text-[rgba(17,19,20,.5)] ">
                <span>{t('loginForm.noAccount')} </span>
                <Link
                  to={`/register${window.location.search}`}
                  className="auth-link text-[rgba(17,19,20,.5)] hover:text-[rgb(0,0,0,.8)]"
                >
                  {t('loginForm.createAccount')}
                </Link>
              </p>

              <Link
                to="/forgot-password"
                className="auth-link text-[12px] font-medium text-[rgba(17,19,20,.5)] hover:text-[rgba(17,19,20,.8)]"
              >
                {t('loginForm.forgotPassword')}
              </Link>
            </div>
          </>
        )}
      </form>
    </AuthFormLayout>
  );
};
